import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OKTA_CONFIG } from '@okta/okta-angular';
import type { OktaAuth } from '@okta/okta-auth-js';
import { Router } from '@angular/router';

// services, models, environment
import { environment } from '../../environments/environment';
import { SessionStorageService } from './session-storage.service';
import { NotificationService } from './notification-service';

import { CaseloadConfiguration, IContactEventConfiguration, User } from '../models/user';
import { Menu } from '../models/menu';
import { Inbox } from '../models/inbox';
import { Client, IContactEvent, IPagedCaseload } from '../models/client';
import { generateObjectHash, IPagedData } from './common-service';



@Injectable( { providedIn: 'root' } )
export class UserService
{

  constructor(
    private _http: HttpClient,
    private _storage: SessionStorageService,
    private _router: Router,
    private _notificationService: NotificationService,
    @Inject( OKTA_CONFIG ) private _oktaConfig: { oktaAuth: OktaAuth },
  )
  {
    this.APP = 'CHR2';
  }

  private API_BASE = environment.apiBaseUrl;
  private API_USER = environment.apiEndpoints.user;
  private APP: string | undefined;


  // public app_logo: string = `assets/ccs-guide-logo.webp`;
  public app_logo: string = `assets/chr-logo.png`;

  public async completeLogin(): Promise<void>
  {
    if ( this._storage.has( 'user' ) )
    {
      return;
    }

    await this._http.post( this.API_BASE + this.API_USER.signIn, { source: this.APP } ).toPromise();

    const user = await this._http.get<User>( this.API_BASE + this.API_USER.profile ).toPromise();
    if ( user )
    {
      this._storage.set( 'user', user );
      this._notificationService.showSuccessMessage( `Welcome to CHR ${user.firstName} ${user.lastName}` );
      await this._router.navigate( ['main'] );
    } else
    {
      await this._router.navigate( ['login'] );
    }
  }

  public async signOut(): Promise<void>
  {
    await this._oktaConfig.oktaAuth.signOut();
    this._storage.clear();
    this._router.navigate( ['login'] );
  }

  public async getMenu(): Promise<Menu | null>
  {

    if ( this._storage.has( 'menu' ) )
    {
      return this._storage.get<Menu>( 'menu' );
    }

    const userId = this.getUser()?.userId || null;

    const menu = await this._http.get<Menu>( `${this.API_BASE}${this.API_USER.menu}/${userId}` ).toPromise();

    if ( menu ) 
    {
      this._storage.set( 'menu', menu );
      return this._storage.get<Menu>( 'menu' );
    }    

    return null;
  }

  public getUser(): User | null
  {
    const user = this._storage.get<User>( 'user' );

    return user;
  }

  public async getInbox( force: boolean = false ): Promise<Inbox | null> 
  {
    if ( this._storage.has( 'inbox' ) && !force )
    {
      return this._storage.get<Inbox>( 'inbox' );
    }

    const inbox = await this._http.get<Inbox>( this.API_BASE + this.API_USER.messages.inbox ).toPromise();
    if ( inbox ) 
    {
      this._storage.set( 'inbox', inbox );
    }
    return this._storage.get<Inbox>( 'inbox' );

  }

  public async getCaseload( config: CaseloadConfiguration ): Promise<IPagedCaseload | undefined>
  {
    const cacheIndex = `caseload#${generateObjectHash( config )}`;
    if ( this._storage.has( cacheIndex ) )
    {
      const cache = this._storage.get( cacheIndex ) || undefined;
      if ( cache )
      {
        this._storage.set( 'caseloadConfiguration', config );
        return cache as IPagedCaseload;
      }
    }

    // fetch from api
    const url = `${this.API_BASE}${this.API_USER.clients.caseload}`
    const response = await this._http.post<any>( url, config ).toPromise();

    if ( response && response.data) {
      // Cache clients separately
      var cachedClients = this._storage.get<Client[]>( 'clients' ) || [];

      // Merge new clients into cache, avoiding duplicates     
      response.data.data.forEach( (client: Client) => {
        if ( !cachedClients.find( c => c.clientId === client.clientId ) ) {
          cachedClients.push( client );
        }
      } );      
      this._storage.set( 'clients', cachedClients );
    }

    this._storage.set( 'caseloadConfiguration', config );
    this._storage.set( cacheIndex, response.data as IPagedCaseload);
    return response.data as IPagedCaseload || undefined;
  }

  public async getClients( config: CaseloadConfiguration, force: boolean = false ): Promise<Client[] | null> 
  {
    if ( this._storage.has( 'clients' ) && !force )
    {
      return this._storage.get<Client[]>( 'clients' );
    }
    const url = `${this.API_BASE}${this.API_USER.clients.caseload}`
    const response = await this._http.post<any>( url, config ).toPromise();
    if ( response ) 
    {
      this._storage.set( 'clients', response.data as Client[] );
    }
    return this._storage.get<Client[]>( 'clients' );

  }

  public async setViewedClient( client: Client ): Promise<void> 
  {
    const viewedClients: Client[] = this._storage.get<Client[]>( 'viewedClients' ) || [];
    if ( !viewedClients.find( c => c.clientId === client.clientId ) )
    {
      viewedClients.push( client );
      this._storage.set( 'viewedClients', viewedClients );
    }
    
  }

  public async getViewedClients(): Promise<Client[] | []> 
  {
    return this._storage.get<Client[]>( 'viewedClients' )||[];
  }

  public async getContactEvents( config: IContactEventConfiguration ): Promise<IPagedData | undefined>
  {
    const cacheIndex = `contactEvents#${generateObjectHash( config )}`;
    if ( this._storage.has( cacheIndex ) )
    {
      const cache = this._storage.get( cacheIndex ) || undefined;
      if ( cache )
      {       
        return cache as IPagedCaseload;
      }
    }

    const url = `${this.API_BASE}${this.API_USER.contactEvents.all}`;
    const response = await this._http.post<any>( url, config ).toPromise();
    if ( response ) 
    {
      this._storage.set( cacheIndex, response.data );
      return response.data as IPagedData;
    }

    return undefined;   
  }

}

